import type { SubtitleStyle } from '../types/videoEdit';

export type SubtitleInlineStylePresetId =
  | 'custom'
  | 'highlight_yellow_black'
  | 'highlight_yellow_red'
  | 'highlight_blue_white'
  | 'highlight_pink_red'
  | 'highlight_mint_black';

export interface SubtitleInlineStyle {
  color?: string;
  fontSize?: number;
  fontScale?: number;
  bold?: boolean;
  italic?: boolean;
  fontName?: string;
  presetId?: SubtitleInlineStylePresetId;
}

export interface SubtitleStyleSpan {
  id: string;
  start: number;
  end: number;
  style: SubtitleInlineStyle;
}

export interface ResolvedSubtitleStyleSpan extends SubtitleStyleSpan {
  start: number;
  end: number;
}

export interface SubtitleTextRun {
  text: string;
  style?: SubtitleInlineStyle;
}

const LEGACY_INLINE_EDITOR_BASE_FONT_SIZE = 18;

interface InlinePresetDefinition {
  id: SubtitleInlineStylePresetId;
  style: SubtitleInlineStyle;
}

const INLINE_PRESET_DEFINITIONS: Record<Exclude<SubtitleInlineStylePresetId, 'custom'>, InlinePresetDefinition> = {
  highlight_yellow_black: {
    id: 'highlight_yellow_black',
    style: { color: '#FFE329', bold: true },
  },
  highlight_yellow_red: {
    id: 'highlight_yellow_red',
    style: { color: '#FFD91A', bold: true },
  },
  highlight_blue_white: {
    id: 'highlight_blue_white',
    style: { color: '#4EA0FF', bold: true },
  },
  highlight_pink_red: {
    id: 'highlight_pink_red',
    style: { color: '#FF91B2', bold: true },
  },
  highlight_mint_black: {
    id: 'highlight_mint_black',
    style: { color: '#67F2CC', bold: true },
  },
};

export const SUBTITLE_INLINE_STYLE_PRESET_IDS = Object.keys(
  INLINE_PRESET_DEFINITIONS,
) as Array<Exclude<SubtitleInlineStylePresetId, 'custom'>>;

export function getSubtitleInlinePresetStyle(
  presetId?: SubtitleInlineStylePresetId,
): SubtitleInlineStyle | undefined {
  if (!presetId || presetId === 'custom') return undefined;
  return INLINE_PRESET_DEFINITIONS[presetId]?.style;
}

export function resolveSubtitleInlineStyle(style?: SubtitleInlineStyle): SubtitleInlineStyle | undefined {
  if (!style) return undefined;
  const presetStyle = getSubtitleInlinePresetStyle(style.presetId);
  return {
    ...presetStyle,
    ...style,
  };
}

export function resolveSubtitleInlineFontScale(
  style?: SubtitleInlineStyle,
  baseFontSize: number = LEGACY_INLINE_EDITOR_BASE_FONT_SIZE,
): number | undefined {
  const resolved = resolveSubtitleInlineStyle(style);
  if (!resolved) return undefined;
  if (typeof resolved.fontScale === 'number' && Number.isFinite(resolved.fontScale)) {
    return resolved.fontScale;
  }
  if (typeof resolved.fontSize === 'number' && Number.isFinite(resolved.fontSize) && baseFontSize > 0) {
    return Number((resolved.fontSize / baseFontSize).toFixed(3));
  }
  return undefined;
}

export function normalizeSubtitleStyleSpans(
  spans: SubtitleStyleSpan[] | undefined,
  text: string,
): ResolvedSubtitleStyleSpan[] {
  if (!spans?.length || !text) return [];

  return spans
    .map((span) => {
      const start = Math.max(0, Math.min(text.length, span.start));
      const end = Math.max(start, Math.min(text.length, span.end));
      return {
        ...span,
        start,
        end,
      };
    })
    .filter((span) => span.end > span.start)
    .sort((a, b) => a.start - b.start || a.end - b.end);
}

export function sliceSubtitleTextRuns(
  text: string,
  spans: SubtitleStyleSpan[] | undefined,
  range?: { start: number; end: number },
): SubtitleTextRun[] {
  const start = range?.start ?? 0;
  const end = range?.end ?? text.length;
  const safeStart = Math.max(0, Math.min(text.length, start));
  const safeEnd = Math.max(safeStart, Math.min(text.length, end));
  const segmentText = text.slice(safeStart, safeEnd);

  if (!segmentText) return [];

  const normalizedSpans = normalizeSubtitleStyleSpans(spans, text)
    .filter((span) => span.end > safeStart && span.start < safeEnd)
    .map((span) => ({
      ...span,
      start: Math.max(span.start, safeStart),
      end: Math.min(span.end, safeEnd),
    }));

  if (!normalizedSpans.length) {
    return [{ text: segmentText }];
  }

  const boundarySet = new Set<number>([safeStart, safeEnd]);
  normalizedSpans.forEach((span) => {
    boundarySet.add(span.start);
    boundarySet.add(span.end);
  });
  const boundaries = Array.from(boundarySet).sort((a, b) => a - b);

  const runs: SubtitleTextRun[] = [];
  for (let i = 0; i < boundaries.length - 1; i++) {
    const runStart = boundaries[i];
    const runEnd = boundaries[i + 1];
    const runText = text.slice(runStart, runEnd);
    if (!runText) continue;

    const coveringSpan = normalizedSpans.find(
      (span) => span.start <= runStart && span.end >= runEnd,
    );

    runs.push({
      text: runText,
      style: resolveSubtitleInlineStyle(coveringSpan?.style),
    });
  }

  return runs;
}

function hexToAliyunBbggrr(hexColor: string): string {
  const normalized = hexColor.replace('#', '');
  const full = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;
  const rr = full.slice(0, 2);
  const gg = full.slice(2, 4);
  const bb = full.slice(4, 6);
  return `${bb}${gg}${rr}`.toUpperCase();
}

export function compileAliyunInlineStyledContent(
  text: string,
  spans: SubtitleStyleSpan[] | undefined,
  baseStyle?: SubtitleStyle,
  range?: { start: number; end: number },
  options?: { exportBaseFontSize?: number },
): string {
  const runs = sliceSubtitleTextRuns(text, spans, range);
  if (!runs.length) return '';

  const baseColor = baseStyle?.color || '#FFFFFF';
  const baseFontSize = baseStyle?.fontSize;

  return runs
    .map((run) => {
      if (!run.style) return run.text;

      const codes: string[] = [];
      const resolved = resolveSubtitleInlineStyle(run.style);
      if (!resolved) return run.text;
      const inferredFontScale = resolveSubtitleInlineFontScale(resolved);

      if (resolved.color && resolved.color !== baseColor) {
        codes.push(`\\1c&${hexToAliyunBbggrr(resolved.color)}&`);
      }
      const resolvedExportFontSize = inferredFontScale && options?.exportBaseFontSize
        ? Math.round(options.exportBaseFontSize * inferredFontScale)
        : resolved.fontSize;
      if (resolvedExportFontSize && resolvedExportFontSize !== baseFontSize) {
        codes.push(`\\fs${resolvedExportFontSize}`);
      }
      if (resolved.bold) {
        codes.push('\\b1');
      }
      if (resolved.italic) {
        codes.push('\\i1');
      }
      if (resolved.fontName) {
        codes.push(`\\fn${resolved.fontName}`);
      }

      const resetCodes: string[] = [];
      if (resolved.color && resolved.color !== baseColor) {
        resetCodes.push('\\1c');
      }
      if (resolvedExportFontSize && resolvedExportFontSize !== baseFontSize) {
        resetCodes.push('\\fs');
      }
      if (resolved.bold) {
        resetCodes.push('\\b0');
      }
      if (resolved.italic) {
        resetCodes.push('\\i0');
      }
      if (resolved.fontName) {
        resetCodes.push('\\fn');
      }

      if (!codes.length) return run.text;
      return `{${codes.join('')}}${run.text}{${resetCodes.join('')}}`;
    })
    .join('');
}
